import { CanvasNode, CanvasGenerationNode, CanvasWallPickerNode } from './canvas-graph.types';

/**
 * How long a failure stays worth re-checking.
 *
 * Long enough to outlast a job that reports failed and then recovers — one did
 * so 24 seconds later — and short enough that a genuine failure stops the
 * polling while the vendor is still looking at it.
 */
export const FAILURE_RECHECK_MS = 3 * 60 * 1000;

/**
 * Is there anything on this canvas still worth asking the server about?
 *
 * Polling used to stop the moment nothing was `pending`, which treated `failed`
 * as the end of the story. It is not always: a job that failed and then
 * succeeded was never asked about again, so the canvas kept the failure, saved
 * it into the graph, and showed a red card for a picture that had generated
 * perfectly. Worse than the wasted work, it made the canvas untrustworthy —
 * every judgement about whether something worked, by a vendor or a test, was
 * being made against a stale answer.
 */
export function shouldKeepPolling(nodes: CanvasNode[], now = Date.now()): boolean {
    return nodes.some((n) => {
        if (n.kind !== 'generation' && n.kind !== 'wall-picker') return false;
        const asset = (n as CanvasGenerationNode | CanvasWallPickerNode).asset;
        if (!asset) return false;
        if (asset.status === 'pending') return true;
        if (asset.status !== 'failed') return false;
        // Undated failures are left alone rather than polled forever.
        const stamp = Date.parse(asset.updatedAt || asset.createdAt || '');
        return Number.isFinite(stamp) && now - stamp < FAILURE_RECHECK_MS;
    });
}
